import type { EventEmitter } from '@widgets/shared/types'
import '@/polyfills/BroadcastChannelPolyfill'
import { getUuid } from '@/utils/uuid'

type EventCode = string
type ListenerId = string
type ListenerCallback<P = any> = (payload: P) => void

interface Listener<P = any> {
  id: ListenerId
  eventCode: EventCode
  callback: ListenerCallback<P>
}

export default class BroadcastEventEmitterService implements EventEmitter {
  protected serviceId = getUuid()

  protected channels: Map<EventCode, BroadcastChannel> = new Map()

  protected listeners: Listener[] = []

  public on<P>(
    eventCode: EventCode,
    callback: ListenerCallback<P>,
    options?: { receiveOwnMessages?: boolean },
  ): ListenerId {
    const id = (Date.now().toString() + Math.random().toString()) as ListenerId
    const receiveOwnMessages = options?.receiveOwnMessages ?? true

    if (!this.channels.has(eventCode)) {
      const channel = new BroadcastChannel(eventCode)
      this.channels.set(eventCode, channel)
    }
    const channel = this.channels.get(eventCode)!

    const broadcastListener = (e: MessageEvent<{ serviceId: string; payload: P }>) => {
      if (!receiveOwnMessages && e.data.serviceId === this.serviceId) return
      callback(e.data.payload)
    }
    channel.addEventListener('message', broadcastListener)

    this.listeners.push({
      id,
      eventCode,
      callback: broadcastListener,
    })
    return id
  }

  public remove(id: ListenerId) {
    const index = this.listeners.findIndex((listener) => listener.id === id)
    if (index === -1) {
      return
    }
    const { eventCode, callback } = this.listeners[index]

    this.channels[eventCode]?.removeEventListener('message', callback)
    this.listeners.slice(index, 1)

    const hasAnotherListeners = this.listeners.some((listener) => listener.eventCode === eventCode)
    if (!hasAnotherListeners) {
      this.channels.get(eventCode)?.close()
      this.channels.delete(eventCode)
    }
  }

  public emit(eventCode: EventCode, payload?: any) {
    this.channels.get(eventCode)!.postMessage({ serviceId: this.serviceId, payload })
  }
}

export const broadcastEventEmitterService = new BroadcastEventEmitterService()
